import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, Minus, Plus, ShoppingBag, ArrowLeft, ShieldCheck, Truck, Sparkles } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { fmtINR, fmtUSD } from "@/lib/money";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [newReview, setNewReview] = useState({ rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const { addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [p, r] = await Promise.all([
          api.get(`/products/${id}`),
          api.get(`/products/${id}/reviews`),
        ]);
        setProduct(p.data);
        setReviews(r.data);
      } catch {
        toast.error("Product not found");
        navigate("/shop");
      }
    })();
  }, [id, navigate]);

  async function submitReview() {
    if (!user) { toast.error("Please login to write a review"); return; }
    if (!newReview.comment.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/products/${id}/reviews`, newReview);
      setReviews((prev) => [data, ...prev]);
      setNewReview({ rating: 5, comment: "" });
      toast.success("Thanks for your review!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  }

  if (!product) return <div className="max-w-7xl mx-auto px-6 py-20 font-display text-xl text-[#4A148C]">Loading…</div>;

  const images = product.images?.length ? product.images.map(fileUrl) : ["https://images.unsplash.com/photo-1605292356183-a77d0a9c9d1d?w=1200&q=80"];
  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "4.8";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <button
        data-testid="pd-back"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm font-body text-[#4A148C] hover:text-[#E65C00] mb-8"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="aspect-square bg-white rounded-3xl overflow-hidden shadow-warm border border-[#FFD1A6]">
            <img src={images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-4">
              {images.map((im, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square rounded-xl overflow-hidden border-2 transition-colors ${activeImg === i ? "border-[#E65C00]" : "border-transparent"}`}
                >
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#4A148C] font-body">{product.category}</div>
          <h1 className="font-display text-5xl text-[#2C1810] mt-2 leading-tight">{product.name}</h1>
          <div className="flex items-center gap-2 mt-3">
            <Star className="w-4 h-4 fill-[#FFB300] text-[#FFB300]" />
            <span className="font-body text-sm text-[#2C1810]">{avgRating}</span>
            <span className="font-body text-sm text-[#5B3D2A]">· {reviews.length} reviews</span>
          </div>
          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-display text-4xl text-[#E65C00] font-semibold">₹{fmtINR(product.price)}</span>
            {product.original_price && product.original_price > product.price && (
              <span className="text-lg text-[#5B3D2A]/60 line-through">₹{fmtINR(product.original_price)}</span>
            )}
          </div>
          <div className="text-xs font-body text-[#5B3D2A]/70 mt-1">≈ ${fmtUSD(product.price)} USD</div>
          <p className="font-body text-[#5B3D2A] mt-6 leading-relaxed">{product.description}</p>

          {product.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {product.tags.map((t) => (
                <span key={t} className="text-xs font-body text-[#4A148C] bg-[#F0E2FA] px-3 py-1 rounded-full">#{t}</span>
              ))}
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center bg-white border border-[#FFD1A6] rounded-full">
              <button data-testid="pd-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 flex items-center justify-center text-[#2C1810] hover:text-[#E65C00]">
                <Minus className="w-4 h-4" />
              </button>
              <span data-testid="pd-qty" className="w-10 text-center font-body">{qty}</span>
              <button data-testid="pd-qty-plus" onClick={() => setQty(Math.min(product.stock || 99, qty + 1))} className="w-10 h-10 flex items-center justify-center text-[#2C1810] hover:text-[#E65C00]">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <Button
              data-testid="pd-add-to-cart"
              onClick={() => { addItem(product, qty); toast.success(`${qty} × ${product.name} added to cart`); }}
              disabled={product.stock === 0}
              className="rounded-full bg-[#E65C00] hover:bg-[#C44A00] text-white flex-1 h-12 btn-lift"
            >
              <ShoppingBag className="w-4 h-4 mr-2" /> {product.stock === 0 ? "Out of stock" : "Add to Cart"}
            </Button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3">
            {[
              { icon: Truck, text: "Free ship > ₹2000" },
              { icon: ShieldCheck, text: "Secure payment" },
              { icon: Sparkles, text: "Handmade" },
            ].map((f) => (
              <div key={f.text} className="flex items-center gap-2 text-xs font-body text-[#5B3D2A] bg-white/70 rounded-xl px-3 py-2 border border-[#FFD1A6]">
                <f.icon className="w-4 h-4 text-[#E65C00]" /> {f.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-20">
        <div className="text-[11px] tracking-[0.25em] uppercase text-[#4A148C] font-body">What customers say</div>
        <h2 className="font-display text-3xl text-[#2C1810] mt-2 mb-8">Reviews</h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {reviews.length === 0 && (
              <div className="p-8 bg-white/60 border border-dashed border-[#FFD1A6] rounded-2xl text-center font-body text-[#5B3D2A]">
                Be the first to review this piece.
              </div>
            )}
            {reviews.map((r) => (
              <div key={r.id} data-testid={`review-${r.id}`} className="bg-white/80 border border-[#FFD1A6] rounded-2xl p-5">
                <div className="flex items-center justify-between">
                  <div className="font-body font-semibold text-[#2C1810]">{r.user_name}</div>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < r.rating ? "fill-[#FFB300] text-[#FFB300]" : "text-[#FFD1A6]"}`} />
                    ))}
                  </div>
                </div>
                <p className="text-sm font-body text-[#5B3D2A] mt-2">{r.comment}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-[#FFF3E0] to-[#FDE9C4] border border-[#FFD1A6] rounded-2xl p-6">
            <div className="font-display text-2xl text-[#2C1810] mb-3">Write a review</div>
            {!user ? (
              <div>
                <p className="text-sm font-body text-[#5B3D2A] mb-4">Login to share your experience.</p>
                <Button data-testid="review-login-btn" onClick={() => navigate("/login")} className="rounded-full bg-[#E65C00] hover:bg-[#C44A00] text-white">Login</Button>
              </div>
            ) : (
              <div>
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      data-testid={`review-rating-${n}`}
                      onClick={() => setNewReview({ ...newReview, rating: n })}
                    >
                      <Star className={`w-6 h-6 ${n <= newReview.rating ? "fill-[#FFB300] text-[#FFB300]" : "text-[#FFD1A6]"}`} />
                    </button>
                  ))}
                </div>
                <Textarea
                  data-testid="review-comment"
                  placeholder="Tell us about your experience…"
                  value={newReview.comment}
                  onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                  rows={4}
                  className="bg-white border-[#FFD1A6]"
                />
                <Button
                  data-testid="review-submit"
                  onClick={submitReview}
                  disabled={submitting || !newReview.comment.trim()}
                  className="w-full mt-3 rounded-full bg-[#4A148C] hover:bg-[#6A2AB0] text-[#FFCF52]"
                >
                  {submitting ? "Posting…" : "Post review"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
